'use client';

import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useGetVolumeByLocationQuery } from 'src/api/redux';
import { PulseIconButton } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import { useTopProviderNavigation } from 'src/context/providers/TopProviderNavigationProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import TopLocationCard from './TopLocationCard';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { buildTopProvidersQueryParams } from '../CompactTopProviders/utils';

const SCROLL_AMOUNT = 300;

const TopLocationsSection = () => {
  const { entityDetails } = useProfile();
  const navigateToLocation = useTopProviderNavigation();
  const widgetConfig = useWidgetConfig();

  const codeSetLabel = widgetConfig?.filterSet?.label
    ? `${widgetConfig.filterSet.label} Claims`
    : 'All Claims';
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { providerId, profileType } = getEntityIdentifiers(entityDetails);

  const filterSet = widgetConfig?.filterSet ?? null;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const providerFilterSetId = widgetConfig?.providerFilterSetId;
  const encounterId = widgetConfig?.widget?.encounter?.id ?? null;

  const queryParams = useMemo(
    () => buildTopProvidersQueryParams(filterSet, minYear, maxYear, providerFilterSetId, encounterId),
    [filterSet, minYear, maxYear, providerFilterSetId, encounterId]
  );

  const { data, isLoading } = useGetVolumeByLocationQuery(
    { providerId, queryParams },
    { skip: !providerId || !profileType }
  );

  const locations =
    data?.results?.filter((loc: Record<string, any>) => loc.provider_id) ?? [];
  const count = locations.length;

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, data]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  const dragState = useRef({
    isDown: false,
    didDrag: false,
    startX: 0,
    scrollLeft: 0,
  });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = {
      isDown: true,
      didDrag: false,
      startX: e.pageX - el.offsetLeft,
      scrollLeft: el.scrollLeft,
    };
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
    el.style.scrollSnapType = 'none';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragState.current.isDown) return;
    dragState.current.didDrag = true;
    const el = scrollRef.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    el.scrollLeft =
      dragState.current.scrollLeft - (x - dragState.current.startX);
  }, []);

  const handleMouseEnd = useCallback(() => {
    dragState.current.isDown = false;
    const el = scrollRef.current;
    if (el) {
      el.style.cursor = 'grab';
      el.style.userSelect = '';
      el.style.scrollSnapType = '';
    }
  }, []);

  const handleCardClick = useCallback(
    (location: Record<string, any>) => {
      if (dragState.current.didDrag) return;
      if (navigateToLocation) {
        navigateToLocation({ provider_id: location.provider_id });
      }
    },
    [navigateToLocation]
  );

  if (isLoading) {
    return (
      <WidgetLoading data-testid="top-locations-section-loading" className="flex items-center justify-center py-8" />
    );
  }

  if (!count) return null;

  return (
    <div data-testid="top-locations-section">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-neutral-800">
                Top Locations
              </h3>
              <span
                className="flex items-center justify-center w-6 h-6 rounded-full bg-sky-600 text-white text-xs font-semibold"
                data-testid="top-locations-count"
                aria-label={`${count} locations`}
              >
                {count}
              </span>
            </div>
            {widgetConfig?.widget?.description && (
              <p className="text-xs text-neutral-500" data-testid="top-locations-description">
                {widgetConfig.widget.description}
              </p>
            )}
          </div>
          {count > 1 && (
            <div className="flex items-center gap-1">
              <PulseIconButton
                variant="outline"
                color="neutral"
                size="sm"
                icon={faChevronLeft}
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                aria-label="Scroll locations left"
                data-testid="top-locations-scroll-left"
                className="focus:ring-0 focus:ring-offset-0"
              />
              <PulseIconButton
                variant="outline"
                color="neutral"
                size="sm"
                icon={faChevronRight}
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                aria-label="Scroll locations right"
                data-testid="top-locations-scroll-right"
                className="focus:ring-0 focus:ring-offset-0"
              />
            </div>
          )}
        </div>
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory cursor-grab py-2 -my-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseEnd}
          onMouseLeave={handleMouseEnd}
        >
          {locations.map(
            (
              location: Record<string, any> & { provider_id: string | number },
              index: number
            ) => (
              <button
                key={`${location.provider_id}-${index}`}
                type="button"
                className="snap-center shrink-0 w-80 text-left cursor-pointer"
                onClick={() => handleCardClick(location)}
                aria-label={`View ${location.name} details`}
              >
                <TopLocationCard
                  location={location}
                  codeSetLabel={codeSetLabel}
                />
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default TopLocationsSection;
